/*
 * Drives the scrolling image row.
 *
 * The CSS keyframe animation is the no-JS baseline. A running CSS animation
 * wins over inline styles, so the two cannot both drive the track: on connect
 * this element adds `image-marquee--js`, which switches the animation off, and
 * takes over with requestAnimationFrame instead. That is what makes dragging
 * possible, since a keyframe animation cannot be nudged mid-flight.
 *
 * The track holds four identical copies of the group, so wrapping the offset
 * modulo one group width always lands on an identical frame and the loop stays
 * seamless no matter where a drag leaves it.
 */
class ImageMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.image-marquee__track');
    if (!this.track) return;

    this.speed = Math.max(parseFloat(this.dataset.speed) || 40, 1);
    this.direction = this.dataset.direction === 'right' ? -1 : 1;
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';

    this.offset = 0;
    this.groupWidth = 0;
    this.hovered = false;
    this.dragging = false;
    this.lastFrame = null;
    this.pointerId = null;

    this.classList.add('image-marquee--js');

    this.onFrame = this.onFrame.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.measure();
    this.observer = new ResizeObserver(() => this.measure());
    this.observer.observe(this.track);

    if (this.pauseOnHover) {
      this.addEventListener('pointerenter', () => {
        this.hovered = true;
      });
      this.addEventListener('pointerleave', () => {
        this.hovered = false;
      });
      this.addEventListener('focusin', () => {
        this.hovered = true;
      });
      this.addEventListener('focusout', () => {
        this.hovered = false;
      });
    }

    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('pointermove', this.onPointerMove);
    this.addEventListener('pointerup', this.onPointerUp);
    this.addEventListener('pointercancel', this.onPointerUp);
    // A drag that ends over a child would otherwise fire a click on it.
    this.addEventListener('click', (event) => {
      if (this.moved) event.preventDefault();
    });

    this.frame = requestAnimationFrame(this.onFrame);
  }

  disconnectedCallback() {
    if (this.frame) cancelAnimationFrame(this.frame);
    if (this.observer) this.observer.disconnect();
  }

  /* One group's width is the loop distance. Its padding-right carries the gap,
     so offsetWidth already includes the spacing between the last and first
     item of consecutive copies. */
  measure() {
    const group = this.querySelector('.image-marquee__group');
    this.groupWidth = group ? group.offsetWidth : 0;
  }

  onFrame(now) {
    this.frame = requestAnimationFrame(this.onFrame);
    if (this.lastFrame === null) this.lastFrame = now;
    const elapsed = (now - this.lastFrame) / 1000;
    this.lastFrame = now;

    if (!this.groupWidth) return;

    const idle = this.hovered || this.dragging || this.reducedMotion.matches;
    if (!idle) {
      // speed is seconds per full loop of one group.
      this.offset += (this.groupWidth / this.speed) * elapsed * this.direction;
    }

    this.apply();
  }

  apply() {
    if (!this.groupWidth) return;
    // Positive modulo so the value stays in range dragging either way.
    this.offset = ((this.offset % this.groupWidth) + this.groupWidth) % this.groupWidth;
    this.track.style.transform = 'translateX(' + -this.offset + 'px)';
  }

  onPointerDown(event) {
    if (event.button !== 0 && event.pointerType === 'mouse') return;
    this.dragging = true;
    this.moved = false;
    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.startOffset = this.offset;
    this.classList.add('image-marquee--dragging');
    this.setPointerCapture(event.pointerId);
  }

  onPointerMove(event) {
    if (!this.dragging || event.pointerId !== this.pointerId) return;
    const delta = event.clientX - this.startX;
    if (Math.abs(delta) > 3) this.moved = true;
    this.offset = this.startOffset - delta;
    this.apply();
  }

  onPointerUp(event) {
    if (!this.dragging || event.pointerId !== this.pointerId) return;
    this.dragging = false;
    this.pointerId = null;
    this.classList.remove('image-marquee--dragging');
    if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
    // Clear on the next tick so the click handler above still sees it.
    setTimeout(() => {
      this.moved = false;
    }, 0);
  }
}

if (!customElements.get('image-marquee')) {
  customElements.define('image-marquee', ImageMarquee);
}
